import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Pathway", to: "/#pathway" },
  { label: "Community", to: "/community" },
  { label: "About", to: "/about" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, to) => {
    if (to.startsWith("/#")) {
      e.preventDefault();
      const id = to.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
      setOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#F6F1EA]/95 backdrop-blur-sm border-b border-[#D7B2A3]/40" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 flex items-center justify-between py-5">
        <Link
          to="/"
          className="flex items-center gap-3 leading-none"
          data-testid="nav-logo"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_session-booking-23/artifacts/q6ceh14a_Jess%20Eisaman%20Co.%20%281%29.png"
            alt="Jess Eisaman Co."
            className="h-12 w-12 object-contain shrink-0"
            data-testid="nav-logo-mark"
          />
          <div className="flex flex-col">
            <span className="font-serif text-xl sm:text-[22px] text-[#4F5D4F] leading-tight">
              Jess Eisaman<span className="text-[#B89B63]"> </span>Co.
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) =>
            l.to.startsWith("/#") ? (
              <a
                key={l.label}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                className="text-sm tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63] transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                className="text-sm tracking-eyebrow uppercase text-[#4F5D4F] hover:text-[#B89B63] transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            )
          )}
          <Link
            to="/pause-the-spiral"
            className="bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-6 py-3 text-xs tracking-eyebrow uppercase transition-colors"
            data-testid="nav-book-button"
          >
            Quiet Pause
          </Link>
        </nav>

        <button
          className="md:hidden text-[#4F5D4F]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F6F1EA] border-t border-[#D7B2A3]/40 px-6 py-8 flex flex-col gap-6">
          {links.map((l) =>
            l.to.startsWith("/#") ? (
              <a
                key={l.label}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.to)}
                className="text-sm tracking-eyebrow uppercase text-[#4F5D4F]"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm tracking-eyebrow uppercase text-[#4F5D4F]"
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            )
          )}
          <Link
            to="/pause-the-spiral"
            onClick={() => setOpen(false)}
            className="bg-[#4F5D4F] text-[#F6F1EA] px-6 py-4 text-xs tracking-eyebrow uppercase text-center"
            data-testid="nav-mobile-book-button"
          >
            Quiet Pause
          </Link>
        </div>
      )}
    </header>
  );
};

export default Nav;
